import { useQuery } from '@apollo/client';
import { AppFooter, SquareLoader } from 'components';
import { NavBar } from 'components/nav/nav-bar';
import { getQuizCodeMaps } from 'graphql/queries';
import { CodeMaps } from 'graphql/queries/types/CodeMaps';
import { ReactNode, createContext, useContext, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from './auth-context';
import { Paper } from '@mui/material';

export interface GlobalContextType extends CodeMaps {
    showNav: boolean;
    quizFocus: boolean;
    eventAlias?: string;
    isEventRoute: boolean;
    toggleNavVisiblity: (visible: boolean) => void;
    toggleQuizFocusVisiblity: (visible: boolean) => void;
    getGenreName: (genreId: number) => string | undefined;
    setNavTitle: (title: string) => void;
}

export const GlobalContext = createContext<GlobalContextType | null>(null);

export const useGlobalContext = () => {
    const context = useContext(GlobalContext);

    if (!context) {
        throw new Error('component must be child of <GlobalContext.Provider />');
    }

    return context;
};

export function GlobalContextProvider({ children, eventAlias }: { children?: ReactNode | undefined; eventAlias?: string }) {
    const {
        loading,
        data,
        error
        // refetch
    } = useQuery<CodeMaps>(getQuizCodeMaps);
    const auth = useAuth();
    const [showNav, toggleNavVisiblity] = useState<boolean>(true);
    const [quizFocus, toggleQuizFocusVisiblity] = useState<boolean>(false);
    const [navTitle, setNavTitle] = useState<string>();
    const isEventRoute = Boolean(eventAlias);

    function getGenreName(genreId: number) {
        return data?.genres?.find((genre) => genre.id === genreId)?.name;
    }

    if (error) {
        console.error('Global context error', error);
        return <Navigate to="/error" />;
    }

    return !loading && !!data ? (
        <GlobalContext.Provider
            value={{
                showNav,
                toggleNavVisiblity,
                quizFocus,
                eventAlias,
                isEventRoute,
                toggleQuizFocusVisiblity,
                ...data,
                getGenreName,
                setNavTitle
            }}
        >
            {!!auth.currentUser && showNav && !quizFocus && <NavBar title={navTitle} />}
            <Paper sx={{ boxShadow: 'none', border: 'none', transition: 'none' }} className={!quizFocus ? 'aq-body' : ''}>
                {children}
            </Paper>
            {!quizFocus && <AppFooter isEventContext={isEventRoute} />}
        </GlobalContext.Provider>
    ) : (
        <SquareLoader color='black' />
    );
}
